import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Bot,
  Check,
  ChevronRight,
  Clock,
  FileText,
  Globe,
  MessageSquare,
  Palette,
  Send,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';

const RevealOnScroll = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setIsVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

const ParticleField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-fuchsia-500/30"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
        50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
      }
    `}</style>
  </div>
);

const CONTACT_EMAILS = ['manu.mani@zoomlocal.in', 'anand.mahajan@zoomlocal.in', 'kedar.deshmukh@zoomlocal.in'];

const EarlyAccessForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    locations: '1-10',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-managed-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          to: CONTACT_EMAILS,
          source: 'agencies-fly-agent-early-access',
        }),
      });

      if (!response.ok) throw new Error('Failed to send');
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('[AgenciesFlyAgentPage] Early access form error:', err);
      setError('Something went wrong. Please try again or reach us on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/30 flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-fuchsia-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">You're on the List!</h3>
        <p className="text-gray-400 max-w-md mx-auto">
          We'll notify you as soon as Fly Agent for Agencies is ready. You'll be among the first to try it.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Your Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors"
            placeholder="Your Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors"
            placeholder="you@agency.com"
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors"
            placeholder="+91 98765 43210"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Agency Name *</label>
          <input
            type="text"
            required
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors"
            placeholder="Your Agency Name"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Number of Client Locations</label>
        <select
          value={formData.locations}
          onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors"
        >
          <option value="1-10">1-10 locations</option>
          <option value="11-50">11-50 locations</option>
          <option value="51-100">51-100 locations</option>
          <option value="100+">100+ locations</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">What AI platforms do you use?</label>
        <textarea
          rows={3}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/50 focus:ring-1 focus:ring-fuchsia-500/25 transition-colors resize-none"
          placeholder="E.g., Claude, ChatGPT, Gemini — and how you'd like to use Fly Agent..."
        />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-500 hover:to-cyan-500 text-white rounded-xl py-6 text-lg"
      >
        {isSubmitting ? 'Sending...' : 'Get Early Access'}
        <Sparkles className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
};

const AgenciesFlyAgentPage = () => {
  return (
    <>
      <Helmet>
        <title>Fly Agent for Agencies | AI-Powered Local SEO (Coming Soon)</title>
        <meta name="description" content="14 specialized AI agents for managing your clients' Google Business Profiles. Works with Claude, ChatGPT, and Gemini. Coming soon — get early access." />
        <meta property="og:title" content="Fly Agent for Agencies | AI-Powered Local SEO" />
        <meta property="og:description" content="AI agents that handle GBP management for your entire client roster. Coming soon." />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white">
        {/* Stripped header — sticky Fly logo only, no nav links. Mirrors
            the focused-landing pattern from /lead-gen-tools so the page
            stays single-funnel without cross-navigation back into the
            marketing site. */}
        <div className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-10 pt-6 pointer-events-none">
          <a href="/" className="inline-flex items-center pointer-events-auto" aria-label="Fly — Agent for Agencies">
            <img
              src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
              alt="Fly"
              className="h-16 md:h-20 brightness-0 invert drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
            />
          </a>
        </div>

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          <ParticleField />

          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(217, 70, 239, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(217, 70, 239, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-fuchsia-950/20 via-transparent to-gray-950" />

          <div className="relative z-10 container mx-auto px-4 text-center">
            <RevealOnScroll>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/20 mb-8">
                <Sparkles className="w-4 h-4 text-fuchsia-400" />
                <span className="text-sm text-fuchsia-300">Coming Soon</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-violet-400 to-cyan-400">
                  AI Agents
                </span>
                <br />
                <span className="text-white">for Your Agency</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-4 leading-relaxed">
                14 specialized AI agents that manage your clients' Google Business Profiles
                through natural conversation with your favorite AI assistant.
              </p>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
                Works with <span className="text-orange-400">Claude</span>,
                <span className="text-green-400"> ChatGPT</span>, and
                <span className="text-blue-400"> Gemini</span>.
                Just talk to your AI — it handles the rest.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a href="#early-access">
                  <Button size="lg" className="bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-500 hover:to-cyan-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-fuchsia-500/25">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Get Early Access
                  </Button>
                </a>
                {/* "See All 14 Agents" intentionally leaks to /fly-agent
                    (the main Fly Agent marketing page) — kept by design
                    so visitors can browse the full agent catalogue. */}
                <Link to="/fly-agent">
                  <Button size="lg" className="bg-transparent border border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl">
                    <Bot className="w-5 h-5 mr-2" />
                    See All 14 Agents
                  </Button>
                </Link>
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-400">Expected launch: Q2 2026</span>
              </div>
            </RevealOnScroll>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-6 h-6 text-gray-600 rotate-90" />
          </div>
        </section>

        {/* What Fly Agent Can Do */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">14 Agents, One Conversation</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Ask your AI assistant anything about your clients' Google profiles — Fly Agent handles the work
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                { icon: Star, title: 'Review Management', description: 'AI-powered responses, auto-responder, sentiment analysis, and review monitoring across all clients.', color: '#f59e0b', gradient: 'from-amber-500 to-orange-400' },
                { icon: TrendingUp, title: 'Rank Tracking', description: 'Track keyword rankings across Google Maps for every client. Geographic grid analysis and competitor tracking.', color: '#3b82f6', gradient: 'from-blue-500 to-cyan-400' },
                { icon: Shield, title: 'Profile Protection', description: 'Monitor all client profiles for unauthorized changes. Instant alerts for critical edits.', color: '#10b981', gradient: 'from-emerald-500 to-teal-400' },
                { icon: MessageSquare, title: 'Content Publishing', description: 'AI generates posts, schedules them, and publishes across client profiles with consistent voice.', color: '#ec4899', gradient: 'from-pink-500 to-rose-400' },
                { icon: FileText, title: 'Branded Reporting', description: 'One-click professional reports with your logo. Monthly summaries, PDF export, and auto-delivery.', color: '#6366f1', gradient: 'from-indigo-500 to-violet-400' },
                { icon: Globe, title: 'Multi-Client Management', description: 'Switch between clients, batch operations, and workspace organization — all through conversation.', color: '#14b8a6', gradient: 'from-teal-500 to-emerald-400' },
              ].map((item, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <div className="relative group h-full">
                    <div className="absolute -inset-0.5 rounded-2xl blur-lg opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                      style={{ background: `linear-gradient(135deg, ${item.color}40, transparent)` }} />
                    <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 p-6 h-full">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} p-2.5 mb-4 shadow-lg`}
                        style={{ boxShadow: `0 8px 32px ${item.color}40` }}
                      >
                        <item.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Works With */}
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-fuchsia-950/10 to-gray-950" />

          <div className="relative container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Bring Your Own AI</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Fly Agent integrates with the AI platforms your team already uses
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { name: 'Claude', protocol: 'MCP Protocol', color: '#f97316' },
                { name: 'ChatGPT', protocol: 'GPT Actions', color: '#10b981' },
                { name: 'Gemini', protocol: 'OpenAPI', color: '#3b82f6' },
              ].map((ai, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <div className="relative h-full">
                    <div className="relative h-full bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 p-8 text-center">
                      <div className="w-16 h-16 rounded-2xl p-3 mb-6 mx-auto" style={{ backgroundColor: `${ai.color}20` }}>
                        <Bot className="w-full h-full" style={{ color: ai.color }} />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">{ai.name}</h3>
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${ai.color}15`, color: ai.color, borderColor: `${ai.color}30`, borderWidth: '1px' }}>
                        {ai.protocol}
                      </div>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Also check out Fly Web */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Need a Dashboard Instead?</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Fly Web gives you a full white-label dashboard to manage clients — available now.
                </p>
              </div>
              <div className="flex justify-center">
                <Link to="/agencies-fly-web" className="group">
                  <div className="relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600/40 to-fuchsia-600/40 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-violet-500/50 transition-all duration-300 p-8 flex items-center gap-6">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-400 p-3 flex-shrink-0">
                        <Palette className="w-full h-full text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">Fly Web for Agencies</h3>
                        <p className="text-gray-400 text-sm">White-label dashboard with your brand, your domain, your clients</p>
                      </div>
                      <div className="flex items-center text-violet-400 text-sm font-medium group-hover:translate-x-2 transition-transform whitespace-nowrap ml-4">
                        Available Now <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Early Access Form */}
        <section id="early-access" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-950/50 via-violet-950/50 to-cyan-950/50" />
          <ParticleField />

          <div className="relative container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <RevealOnScroll>
                <div className="text-center mb-10">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                    Get Early Access
                  </h2>
                  <p className="text-lg text-gray-400">
                    Be among the first agencies to use Fly Agent. Sign up and we'll notify you when it's ready.
                  </p>
                </div>

                <div className="bg-gray-900/80 backdrop-blur-sm rounded-3xl border border-gray-800 p-8 md:p-10">
                  <EarlyAccessForm />
                </div>

                {/* WhatsApp Alternative */}
                <div className="mt-8 text-center">
                  <p className="text-gray-500 text-sm mb-4">Or reach us directly on WhatsApp</p>
                  <a
                    href="https://wa.me/918446712833?text=Hi%2C%20I%27m%20interested%20in%20early%20access%20to%20Fly%20Agent%20for%20my%20agency"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gray-900/80 backdrop-blur-sm border border-gray-800 hover:border-fuchsia-500/50 transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-fuchsia-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold text-sm group-hover:text-fuchsia-400 transition-colors">Chat on WhatsApp</p>
                      <p className="text-gray-500 text-xs">+91 84467 12833</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-fuchsia-400 group-hover:translate-x-1 transition-all" />
                  </a>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </section>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default AgenciesFlyAgentPage;
